import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { useAddons } from "@/contexts/AddonsContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Plus, PencilSimpleLine, Trash, ToggleRight, ToggleLeft, PlayCircle,
  Warning, ShieldCheck, Calculator, ClipboardText,
} from "@phosphor-icons/react";

/**
 * Phase 4 — No-code Salary Rule Builder page.
 *
 * Access is DOUBLE-gated:
 *   1. AppShell hides the sidebar item unless `hr_rule_manager` >= view.
 *   2. This page also checks `hr_rule_manager` >= view on mount and
 *      shows a clear "role required" screen when the caller lacks it.
 *      This guarantees direct URL entry (/workforce/salary-rules) is
 *      blocked too.
 */
const RULE_TYPE_META = {
  commission: { label: "Commission",  color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  bonus:      { label: "Bonus",       color: "bg-blue-100 text-blue-800 border-blue-200" },
  deduction:  { label: "Deduction",   color: "bg-rose-100 text-rose-800 border-rose-200" },
  overtime:   { label: "Overtime",    color: "bg-purple-100 text-purple-800 border-purple-200" },
  allowance:  { label: "Allowance",   color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  custom:     { label: "Custom",      color: "bg-slate-100 text-slate-800 border-slate-200" },
};

const OPERATOR_LABELS = {
  eq:  "equals",
  gt:  "greater than",
  lt:  "less than",
  gte: "greater than or equal to",
  lte: "less than or equal to",
  between: "between",
};

const EMPTY_RULE = {
  name: "",
  description: "",
  type: "bonus",
  active: true,
  trigger: { variable: "attendance_days_present", operator: "gte", value: 1 },
  formula: { amount_type: "fixed", amount: 0, applies_to: "custom_base", custom_base: 0 },
  scope: { employees: [], designations: [], departments: [], branch_ids: [], employment_types: [] },
  tiers: [],
  sort_order: 100,
  // iter-67i (ID-38) — Optional. When set, the payroll engine skips
  // this rule for any period whose end predates this ISO date.
  effective_from: "",
};

export default function SalaryRulesPage() {
  const { can } = usePermissions();
  const { hasEnterpriseWorkforce } = useAddons();
  const navigate = useNavigate();

  const [vars, setVars] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [rules, setRules] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // rule object being edited/created
  const [tab, setTab] = useState("all");

  const hasHrRule = can("hr_rule_manager", "view");
  const canManage = can("hr_rule_manager", "full");

  const loadAll = async () => {
    setLoading(true);
    try {
      const [rulesR, varsR, tplR, cR] = await Promise.all([
        api.get("/salary-rules"),
        api.get("/salary-rules/variables"),
        api.get("/salary-rules/statutory-templates"),
        // Conflicts endpoint requires manage — non-managers see empty.
        canManage ? api.get("/salary-rules/conflicts") : Promise.resolve({ data: { conflicts: [] } }),
      ]);
      setRules(rulesR.data || []);
      setVars(varsR.data);
      setTemplates(tplR.data.templates || []);
      setConflicts(cR.data.conflicts || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasHrRule) { setLoading(false); return; }
    loadAll();
     
  }, [hasHrRule]);

  // ---- ADD-ON + PERMISSION GUARDS ----
  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading Salary Rule Builder…</div>;
  }
  if (!hasEnterpriseWorkforce) {
    return (
      <div className="p-6">
        <Alert>
          <AlertDescription>
            The Salary Rule Builder is part of the Enterprise Workforce Suite add-on.
            Ask your workspace owner to activate the add-on to unlock it.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!hasHrRule) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center space-y-3" data-testid="hr-rule-manager-required">
        <ShieldCheck size={44} weight="duotone" className="mx-auto text-primary" />
        <h1 className="text-xl font-semibold">HR Rule Manager role required</h1>
        <p className="text-sm text-muted-foreground">
          The Salary Rule Builder is restricted to users assigned the
          <b> HR_Rule_Manager</b> role. Even Admins, Payroll Managers and HR
          Managers do not have access unless they are also assigned this role.
        </p>
        <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
      </div>
    );
  }

  const filtered = rules.filter(r => tab === "all" || r.type === tab);

  const startEdit  = (r = null)   => setEditing(r ? { ...r } : { ...EMPTY_RULE });
  const closeEdit  = ()          => setEditing(null);
  const fromTemplate = (t) => setEditing({ ...EMPTY_RULE, ...t, active: true, tiers: t.tiers || [] });

  const removeRule = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try {
      await api.delete(`/salary-rules/${r.id}`);
      toast.success("Rule deleted");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleRule = async (r) => {
    try {
      await api.post(`/salary-rules/${r.id}/toggle`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="salary-rules-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Calculator size={22} weight="duotone" className="text-primary" />
            Salary Rule Builder
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl mt-1">
            Design commission, bonus, deduction and overtime rules visually — no code, no formula syntax.
            Session variables are commission-only, so base salary is never linked to how many sessions a therapist runs.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => startEdit()} data-testid="new-rule-btn">
            <Plus size={16} className="mr-1" /> New Rule
          </Button>
        )}
      </header>

      {conflicts.length > 0 && (
        <Alert className="border-amber-300 bg-amber-50" data-testid="conflicts-banner">
          <Warning size={16} className="text-amber-700" />
          <AlertDescription className="text-amber-800">
            <b>{conflicts.length} rule conflict{conflicts.length > 1 ? "s" : ""} detected.</b> Two active rules
            share the same trigger and scope — payroll would apply them twice. Resolve before running payroll.
            <ul className="mt-2 text-xs list-disc pl-5 space-y-0.5">
              {conflicts.slice(0, 5).map((c) => (
                <li key={`${c.rule_a.id}-${c.rule_b.id}`}>&ldquo;{c.rule_a.name}&rdquo; ↔ &ldquo;{c.rule_b.name}&rdquo; — {c.reason}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="rules-tab-all">All ({rules.length})</TabsTrigger>
          {Object.keys(RULE_TYPE_META).map(k => (
            <TabsTrigger key={k} value={k} data-testid={`rules-tab-${k}`}>
              {RULE_TYPE_META[k].label} ({rules.filter(r => r.type === k).length})
            </TabsTrigger>
          ))}
          <TabsTrigger value="templates" data-testid="rules-tab-templates">Statutory templates</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4 space-y-2">
          {tab === "templates" ? (
            <StatutoryTemplatesList templates={templates} onUse={fromTemplate} disabled={!canManage} />
          ) : filtered.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-muted-foreground">
              No {tab === "all" ? "" : RULE_TYPE_META[tab]?.label?.toLowerCase()} rules yet.
              {canManage && <> Click <b>New Rule</b> to build one visually, or start from a statutory template.</>}
            </CardContent></Card>
          ) : (
            filtered.map(r => (
              <RuleRow key={r.id} rule={r} vars={vars}
                onEdit={() => startEdit(r)} onToggle={() => toggleRule(r)}
                onDelete={() => removeRule(r)} canManage={canManage}/>
            ))
          )}
        </TabsContent>
      </Tabs>

      {editing && (
        <RuleBuilderDialog
          rule={editing}
          vars={vars}
          onClose={closeEdit}
          onSaved={() => { closeEdit(); loadAll(); }}
        />
      )}
    </div>
  );
}

// ============================================================
// Rule row
// ============================================================
function RuleRow({ rule, vars, onEdit, onToggle, onDelete, canManage }) {
  const meta = RULE_TYPE_META[rule.type] || RULE_TYPE_META.custom;
  const variable = vars?.variables?.find(v => v.key === rule.trigger?.variable);
  return (
    <Card className={rule.active ? "border-emerald-200" : "opacity-70 border-dashed"} data-testid={`rule-row-${rule.id}`}>
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.color}`}>{meta.label}</span>
            <span className="font-medium">{rule.name}</span>
          </div>
          {rule.description && <div className="text-xs text-muted-foreground mt-0.5">{rule.description}</div>}
          <div className="text-xs mt-1 text-muted-foreground">
            When <b>{variable?.label || rule.trigger?.variable}</b> {OPERATOR_LABELS[rule.trigger?.operator]}{" "}
            <b>{Array.isArray(rule.trigger?.value) ? rule.trigger.value.join("–") : rule.trigger?.value}</b>
            {" → "}
            {rule.formula.amount_type === "percentage"
              ? <b>{rule.formula.amount}%</b>
              : <b>₹{rule.formula.amount}</b>}
            {" of "}<b>{rule.formula.applies_to.replace(/_/g, " ")}</b>
            {rule.tiers?.length > 0 && <> · <b>{rule.tiers.length}-tier</b></>}
          </div>
          {rule.effective_from && (
            <div className="text-[11px] mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800" data-testid={`rule-effective-${rule.id}`}>
              Effective from {rule.effective_from}
            </div>
          )}
        </div>
        <div className="flex-none flex items-center gap-2">
          {canManage && (
            <>
              {/* Prominent active/inactive pill — clearly indicates state
                  and doubles as the toggle. Bigger + label per Iter-42. */}
              <button
                type="button"
                onClick={onToggle}
                data-testid={`rule-toggle-${rule.id}`}
                title="Click to activate/deactivate"
                aria-pressed={rule.active}
                className={
                  "inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors " +
                  (rule.active
                    ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200"
                    : "bg-muted text-muted-foreground border-border hover:bg-muted/80")
                }
              >
                {rule.active
                  ? <ToggleRight size={22} weight="fill" className="text-emerald-600" />
                  : <ToggleLeft  size={22} weight="fill" className="text-muted-foreground" />}
                <span className="tracking-wide uppercase">{rule.active ? "Active" : "Inactive"}</span>
              </button>
              <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`rule-edit-${rule.id}`}>
                <PencilSimpleLine size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={onDelete} className="text-rose-600" data-testid={`rule-delete-${rule.id}`}>
                <Trash size={16} />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// Statutory templates list
// ============================================================
function StatutoryTemplatesList({ templates, onUse, disabled }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {templates.map(t => (
        <Card key={t.template_key} data-testid={`template-${t.template_key}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full border ${RULE_TYPE_META[t.type]?.color || ""}`}>
                {RULE_TYPE_META[t.type]?.label || t.type}
              </span>
              <ClipboardText size={14} className="text-muted-foreground" />
              <span className="font-medium">{t.name}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">{t.description}</div>
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={() => onUse(t)} disabled={disabled} data-testid={`template-use-${t.template_key}`}>
                Use as starting point
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ============================================================
// Rule Builder Dialog — the visual builder
// ============================================================
function RuleBuilderDialog({ rule, vars, onClose, onSaved }) {
  const [form, setForm] = useState(rule);
  const [preview, setPreview] = useState(null);
  const [previewSample, setPreviewSample] = useState({
    attendance_days_present: 22, leave_days_taken: 2, unpaid_leave_days: 0,
    late_arrivals: 0, early_exits: 0, total_hours_worked: 176, overtime_hours: 4,
    sessions_completed: 25, session_revenue: 50000, base_salary: 30000, basic_pay: 18000, hourly_rate: 200,
  });
  const [saving, setSaving] = useState(false);

  const editing = !!form.id;

  const set = (patch) => setForm(f => ({ ...f, ...patch }));
  const setTrigger = (patch) => setForm(f => ({ ...f, trigger: { ...f.trigger, ...patch } }));
  const setFormula = (patch) => setForm(f => ({ ...f, formula: { ...f.formula, ...patch } }));

  const sessionVars = new Set(vars?.session_variables || []);
  const isSessionVarAllowed = form.type === "commission";

  const variableChoices = useMemo(() => {
    if (!vars) return [];
    // For scope predicates we don't include branch/department/etc. as
    // triggers. Only numeric-ish variables.
    return vars.variables.filter(v => !["branch", "department", "designation", "employment_type"].includes(v.key));
  }, [vars]);

  const runPreview = async () => {
    try {
      const { data } = await api.post("/salary-rules/preview", { rule: form, sample: previewSample });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setPreview(null);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the rule a name"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/salary-rules/${form.id}`, form);
      else         await api.post(`/salary-rules`, form);
      toast.success(editing ? "Rule updated" : "Rule created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rule-builder-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit rule" : "New rule"}</DialogTitle>
          <DialogDescription>
            Build the rule visually. Preview against a sample employee before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          {/* LEFT: Rule fields */}
          <div className="space-y-3">
            <div>
              <Label>Rule name</Label>
              <Input value={form.name} onChange={e => set({ name: e.target.value })} data-testid="rule-name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description || ""} onChange={e => set({ description: e.target.value })} data-testid="rule-description" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Rule type</Label>
                <Select value={form.type} onValueChange={v => set({ type: v })}>
                  <SelectTrigger data-testid="rule-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(RULE_TYPE_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label>Sort order</Label>
                  <Input type="number" value={form.sort_order} onChange={e => set({ sort_order: parseInt(e.target.value) || 100 })} data-testid="rule-sort" />
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Switch checked={form.active} onCheckedChange={v => set({ active: v })} data-testid="rule-active" />
                  <Label className="text-xs">Active</Label>
                </div>
              </div>
            </div>
            <div>
              <Label>Effective from (optional)</Label>
              <Input
                type="date"
                value={form.effective_from || ""}
                onChange={e => set({ effective_from: e.target.value })}
                data-testid="rule-effective-from"
              />
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Blank means the rule is always active. When set, the payroll engine skips it for periods that end before this date.
              </div>
            </div>

            <hr />

            <div>
              <Label className="font-semibold">Trigger (WHEN)</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Select value={form.trigger.variable} onValueChange={v => setTrigger({ variable: v })}>
                  <SelectTrigger data-testid="rule-trigger-var"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {variableChoices.map(v => (
                      <SelectItem key={v.key} value={v.key} disabled={sessionVars.has(v.key) && !isSessionVarAllowed}>
                        {v.label}{sessionVars.has(v.key) ? " (commission only)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={form.trigger.operator} onValueChange={v => setTrigger({ operator: v })}>
                  <SelectTrigger data-testid="rule-trigger-op"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(vars?.operators || []).map(o => <SelectItem key={o} value={o}>{OPERATOR_LABELS[o]}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.trigger.operator === "between" ? (
                  <div className="flex gap-1">
                    <Input type="number" placeholder="min" value={form.trigger.value?.[0] ?? 0}
                      onChange={e => setTrigger({ value: [parseFloat(e.target.value) || 0, form.trigger.value?.[1] ?? 0] })} />
                    <Input type="number" placeholder="max" value={form.trigger.value?.[1] ?? 0}
                      onChange={e => setTrigger({ value: [form.trigger.value?.[0] ?? 0, parseFloat(e.target.value) || 0] })} />
                  </div>
                ) : (
                  <Input type="number" value={form.trigger.value} onChange={e => setTrigger({ value: parseFloat(e.target.value) || 0 })} data-testid="rule-trigger-value" />
                )}
              </div>
              {sessionVars.has(form.trigger.variable) && !isSessionVarAllowed && (
                <div className="mt-1 text-xs text-rose-600" data-testid="session-var-warning">
                  Session-related variables can only be used inside <b>Commission</b> rules. Change the rule type first.
                </div>
              )}
            </div>

            <hr />

            <div>
              <Label className="font-semibold">Result (THEN pay)</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <Select value={form.formula.amount_type} onValueChange={v => setFormula({ amount_type: v })}>
                  <SelectTrigger data-testid="rule-amount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed amount (₹)</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
                <Input type="number" value={form.formula.amount}
                  onChange={e => setFormula({ amount: parseFloat(e.target.value) || 0 })}
                  placeholder={form.formula.amount_type === "percentage" ? "%" : "₹"}
                  data-testid="rule-amount"
                />
                <Select value={form.formula.applies_to} onValueChange={v => setFormula({ applies_to: v })}>
                  <SelectTrigger data-testid="rule-applies-to"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic_pay">of Basic Pay (line-item only)</SelectItem>
                    <SelectItem value="base_salary">of Net base salary (all earnings − base deductions)</SelectItem>
                    <SelectItem value="session_revenue" disabled={!isSessionVarAllowed}>of Session revenue (commission only)</SelectItem>
                    <SelectItem value="hourly_rate">× Overtime hours (uses hourly rate)</SelectItem>
                    <SelectItem value="custom_base">Custom base</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.formula.applies_to === "custom_base" && (
                <div className="mt-2">
                  <Label className="text-xs">Custom base value (₹)</Label>
                  <Input type="number" value={form.formula.custom_base || 0}
                    onChange={e => setFormula({ custom_base: parseFloat(e.target.value) || 0 })}
                    data-testid="rule-custom-base" />
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Preview */}
          <div className="space-y-3">
            <div className="rounded border p-3 bg-muted/30">
              <div className="text-xs uppercase font-semibold text-muted-foreground mb-2">Sample employee metrics</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  ["attendance_days_present", "Days present"],
                  ["unpaid_leave_days", "Unpaid leaves"],
                  ["total_hours_worked", "Hours worked"],
                  ["overtime_hours", "Overtime hours"],
                  ["sessions_completed", "Sessions completed"],
                  ["session_revenue", "Session revenue (₹)"],
                  ["base_salary", "Basic salary (₹)"],
                  ["basic_pay", "Basic Pay line (₹)"],
                  ["hourly_rate", "Hourly rate (₹)"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1">
                    <span className="text-muted-foreground w-24">{label}</span>
                    <Input type="number" value={previewSample[k]}
                      onChange={e => setPreviewSample(s => ({ ...s, [k]: parseFloat(e.target.value) || 0 }))}
                      className="h-7 text-xs"
                      data-testid={`preview-input-${k}`}
                    />
                  </label>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={runPreview} className="mt-3 w-full" data-testid="preview-btn">
                <PlayCircle size={14} className="mr-1" /> Preview calculation
              </Button>
            </div>

            {preview && (
              <div
                className={`rounded border p-3 ${preview.matched ? "bg-emerald-50 border-emerald-200" : "bg-slate-50"}`}
                data-testid="preview-result"
              >
                <div className="text-xs uppercase font-semibold text-muted-foreground mb-1">Result</div>
                <div className="text-2xl font-semibold" data-testid="preview-amount">
                  ₹{preview.amount?.toLocaleString("en-IN")}
                </div>
                {preview.notes && (
                  <div className="mt-1 text-xs text-muted-foreground">{preview.notes.join(" · ")}</div>
                )}
                {!preview.matched && (
                  <div className="mt-1 text-xs text-rose-700">{preview.reason}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rule-save-btn">
            {saving ? "Saving…" : (editing ? "Save changes" : "Create rule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
